// What the haystack page (haystack.ts, the demo's page: src/index.html) is
// built on: the search worker (../search_worker.ts) behind a few callbacks,
// and the small helpers its rendering uses.

import { cachedBytes, clearCache } from "../cached_fetch";
import { config } from "../config";

export interface Doc {
  DocID: number;
  URL: string;
  Score: number;
  Tag: string;
}

export interface Spelling {
  Corrected: string;
  // Applied: the search ran on Corrected ("Showing results for"); otherwise
  // it ran as typed and Corrected is only offered ("Did you mean").
  Applied: boolean;
  Changes: { From: string; To: string; Index: number }[];
}

export interface Result {
  Query: string;
  Searched?: string;
  // The web address the query itself is, offered above the results.
  Direct?: string;
  Spelling?: Spelling | null;
  // The query reads as someone in crisis: a helpline notice goes first.
  Crisis?: boolean;
  // A site: or filetype: filter matched nothing fetched; shown unfiltered.
  Unfiltered?: boolean;
  Docs: Doc[];
  More?: Doc[] | null;
  Timing: { Total: number }; // ns
  Comm: { Upload: number; Download: number }; // bytes
}

// The first visit's load, as one fraction that only grows: the client is the
// first CLIENT_SHARE, the deployment's files the next dataShare (most of the
// bar when they are downloaded, less when they come from the cache), the
// on-device setup the rest.
export interface LoadState {
  frac: number;
  title: string;
  detail: string;
}

// The phases of one search (searchclient.Step*), in the order the client
// runs them. "Locally": nothing about it leaves this device.
export const STEPS: { id: string; short: string; text: string }[] = [
  { id: "embed", short: "Embedding", text: "Embedding the query as a vector, locally" },
  { id: "centroids", short: "Choosing clusters", text: "Scoring the query against the cluster centroids, locally" },
  { id: "keywords", short: "Looking up words", text: "PIR lookups of the query's words and names" },
  { id: "query", short: "Encrypting", text: "Encrypting the query vector" },
  { id: "answer", short: "Servers working blind", text: "Servers compute encrypted scores over the whole index" },
  { id: "reconstruct", short: "Decrypting", text: "Decrypting the chosen clusters' scores, locally" },
  { id: "rank", short: "Ranking", text: "Fusing dense, keyword and entity scores, locally" },
  { id: "urls", short: "Fetching addresses", text: "Fetching the top results' URLs by PIR" },
  { id: "rerank", short: "Reranking", text: "Reranking with a cross-encoder, locally" },
];

export interface EngineHooks {
  onLoad(s: LoadState): void;
  onReady(numDocs: number): void;
  onLoadError(message: string): void;
  // This browser is not keeping the downloads (a private window, a full
  // disk): every visit downloads them again.
  onNotKept(): void;
}

export interface Engine {
  // onStep gets each phase as it starts, with its index in STEPS (-1 for one
  // not listed there).
  // verbatim searches the query exactly as typed, with no spelling correction.
  search(query: string, onStep: (id: string, index: number) => void, verbatim?: boolean): Promise<Result>;
  stored(): Promise<number>;
  clearStored(): Promise<void>;
  // retry starts a load that failed over, in a fresh worker. The files the
  // failed one finished are kept (Cache Storage), so only the rest download.
  retry(): void;
}

const CLIENT_SHARE = 0.05;
const SETUP: [string, string, number][] = [
  ["setup:tables", "Reading the routing tables", 0.6],
  ["setup:scores", "Decoding the scoring hints", 1.5],
  ["setup:urls", "Decoding the URL table's hints", 2.4],
  ["setup:entities", "Decoding the entity table's hints", 0.75],
  ["setup:terms", "Decoding the keyword table's hints", 0.25],
  ["setup:models", "Loading the models", 0.3],
];
const SETUP_TOTAL = SETUP.reduce((a, s) => a + s[2], 0);

export const MB = (n: number) => (n / 1e6).toFixed(n < 10e6 ? 1 : 0);

export function startEngine(h: EngineHooks): Engine {
  let worker: Worker;
  let pending: { resolve: (r: Result) => void; reject: (e: Error) => void; onStep: (id: string, i: number) => void } | null = null;
  let at = 0, dataShare = 0.8, dataStarted = false, clientFromCache = false, setupStarted = false;
  let returning = false;

  const load = (frac: number, title: string, detail: string) => {
    at = Math.max(at, Math.min(frac, 1));
    h.onLoad({ frac: at, title, detail });
  };

  const onMessage = (e: MessageEvent) => {
    const m = e.data;
    switch (m.type) {
      case "progress": {
        if (setupStarted) return;
        const f = m.total > 0 ? Math.min(m.loaded / m.total, 1) : 0;
        if (m.step === "client") {
          clientFromCache = m.fromCache > 0;
          load(f * CLIENT_SHARE, "Fetching the client", `${MB(m.loaded)} of ${MB(m.total)} MB`);
        } else {
          if (!dataStarted) {
            dataStarted = true;
            dataShare = clientFromCache ? 0.3 : 0.8;
          }
          const downloading = m.loaded > m.fromCache;
          load(
            CLIENT_SHARE + f * dataShare,
            returning && !downloading ? "Checking the saved index" : "Bringing the index into this browser",
            `${MB(m.loaded)} of ${MB(m.total)} MB · ${m.done} of ${m.files} files`,
          );
        }
        break;
      }
      case "step":
        if (m.step === "connect") load(CLIENT_SHARE + dataShare, "Preparing", "Building the encryption state, on this device");
        break;
      case "searchstep":
        if (m.step.startsWith("setup:")) {
          setupStarted = true;
          const i = SETUP.findIndex((s) => s[0] === m.step);
          if (i < 0) return;
          const done = SETUP.slice(0, i).reduce((a, s) => a + s[2], 0);
          const base = CLIENT_SHARE + dataShare;
          load(base + (done / SETUP_TOTAL) * (1 - base), "Preparing", SETUP[i][1]);
        } else {
          pending?.onStep(m.step, STEPS.findIndex((s) => s.id === m.step));
        }
        break;
      case "log":
        console.log("[client]", m.line);
        break;
      case "storage":
        if (!m.kept) h.onNotKept();
        break;
      case "ready":
        load(1, "Ready", "");
        h.onReady(m.numDocs ?? 0);
        navigator.storage?.persist?.().catch(() => {});
        break;
      case "results":
        pending?.resolve(m.result);
        pending = null;
        break;
      case "error":
        if (m.stack) console.error(m.stack);
        if (pending) {
          pending.reject(new Error(m.error));
          pending = null;
        } else {
          h.onLoadError(
            /fetch\(\) failed/.test(m.error)
              ? `Couldn't reach the server at ${config.coordinator}.`
              : `Loading didn't finish: ${m.error}`,
          );
        }
        break;
    }
  };

  // A load from the start, in a new worker. The progress starts over too:
  // a retry's bar counts what is downloaded and checked this time.
  const spawn = () => {
    at = 0;
    dataShare = 0.8;
    dataStarted = clientFromCache = setupStarted = false;
    cachedBytes().then((n) => (returning = n > 0)).catch(() => {});
    worker = new Worker(new URL("../search_worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = onMessage;
    worker.postMessage({
      type: "init",
      coordinator: config.coordinator,
      tls: config.tls,
      modelsBase: config.modelsBase,
      embedModel: config.embedModel,
      ceModel: config.ceModel,
    });
  };
  spawn();

  return {
    search(query, onStep, verbatim) {
      if (pending) return Promise.reject(new Error("a search is already running"));
      return new Promise((resolve, reject) => {
        pending = { resolve, reject, onStep };
        worker.postMessage({ type: "search", query, verbatim: !!verbatim });
      });
    },
    stored: () => cachedBytes(),
    clearStored: () => clearCache(),
    retry() {
      // The failed worker goes, with any rerank helpers it started.
      worker.terminate();
      pending = null;
      spawn();
    },
  };
}

// ── Shared bits of rendering ──

// Every fetched result, the top ones first: the pages past the first are
// bytes the URL rounds already brought back, so paging asks the servers for
// nothing.
export const allDocs = (r: Result) => r.Docs.concat(r.More ?? []);

// A result's link, split for display: "example.com" and "/the/path", with no
// scheme and no "www.".
export function splitURL(url: string): { href: string; host: string; path: string } {
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  const bare = url.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  const cut = bare.search(/[/?#]/);
  return { href, host: cut < 0 ? bare : bare.slice(0, cut), path: cut < 0 ? "" : bare.slice(cut) };
}

export function link(url: string, className: string, children: (Node | string)[]): HTMLAnchorElement {
  const a = document.createElement("a");
  a.className = className;
  a.href = splitURL(url).href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.append(...children);
  return a;
}

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls = "", text = ""): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
}

export const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

// Compact counts: 1.2B, 340M.
export function count(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(n >= 1e10 ? 0 : 1).replace(/\.0$/, "")}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1).replace(/\.0$/, "")}M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)}K`;
  return String(n);
}
